import { useEffect, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { BASE_PATH, type EventInput, type EventRecord } from '../../shared/types';
import { validateDraftShape, validateEventInput } from '../../shared/validate';
import { defaultLeader, defaultUnit, rememberedWardNames, type UnitHistoryItem } from '../../shared/suggest';
import { sortEventDates } from '../../shared/dates';
import { sortWardNames } from '../../shared/wards';
import { migratePdfEventDraft, validatePdfEventDraftShape } from '../../shared/pdfDrafts';
import { api, ApiError } from './api';
import { DraftSaver, saveEventDraft, type SaveState } from './draftSaver';
import { eventInput } from './eventInput';
import { reportClientError } from './clientErrors';
export { eventInput };

function initialInput(email: string, history: UnitHistoryItem[]): EventInput {
  const unit = defaultUnit(history), leader = defaultLeader(history);
  return migratePdfEventDraft({ level: 'ward', stakeName: unit.stakeName, wardName: unit.wardName, wardNames: rememberedWardNames(history, unit.stakeName),
    name: '', dates: [{ start: '' }], description: '', leaderName: leader.leaderName, leaderPhone: leader.leaderPhone, leaderEmail: email, fields: [] });
}
const key = (email: string, id: string) => `cpf:event-draft:${email}:${id}`;

export function useEventDraft(id: string | undefined, email: string | undefined) {
  const [input, setInput] = useState<EventInput | null>(null);
  const [record, setRecord] = useState<EventRecord | null>(null);
  const [history, setHistory] = useState<UnitHistoryItem[]>([]);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [recovered, setRecovered] = useState(false);
  const [backupAvailable, setBackupAvailable] = useState(true);
  const [recoveryConflict, setRecoveryConflict] = useState<EventRecord | null>(null);
  const saver = useRef<DraftSaver | null>(null);
  const current = useRef<EventInput | null>(null);
  const mounted = useRef(true);
  const errorRef = useRef(saveError);
  errorRef.current = saveError;

  useEffect(() => {
    mounted.current = true;
    const pagehide = () => { void saver.current?.flush().catch(() => {}); };
    const beforeunload = (e: BeforeUnloadEvent) => {
      if (errorRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('pagehide', pagehide);
    window.addEventListener('beforeunload', beforeunload);
    return () => {
      mounted.current = false;
      window.removeEventListener('pagehide', pagehide);
      window.removeEventListener('beforeunload', beforeunload);
      saver.current?.dispose();
    };
  }, []);

  useEffect(() => {
    if (!email) return;
    if (id && id !== 'new' && saver.current?.id === id && current.current) return;
    let active = true;
    saver.current?.dispose();
    saver.current = null;
    current.current = null;
    setInput(null); setRecord(null); setLoadError(null); setRecovered(false); setRecoveryConflict(null);
    const load = async () => {
      const newEvent = !id || id === 'new';
      let draftId = id!;
      if (newEvent) {
        try { draftId = localStorage.getItem(key(email, 'new')) || crypto.randomUUID(); }
        catch { draftId = crypto.randomUUID(); setBackupAvailable(false); }
      }
      const evs = await api.get<EventRecord[]>('/events');
      let existing = newEvent ? evs.find((r) => r.id === draftId) : await api.get<EventRecord>(`/events/${draftId}`);
      if (!active) return;
      const previous = evs.filter((r) => r.id !== draftId && !validateEventInput(eventInput(r)).length);
      setHistory(previous);
      const baseline = existing ? eventInput(existing) : initialInput(email, previous);
      let restored: EventInput | null = null;
      let backupVersion = existing?.draftVersion ?? 0;
      try {
        const backup = JSON.parse(localStorage.getItem(key(email, draftId)) || 'null');
        if (backup && !validatePdfEventDraftShape(backup.input).length) {
          const candidate = migratePdfEventDraft({ ...backup.input, dates: sortEventDates(backup.input.dates), wardNames: sortWardNames(backup.input.wardNames) });
          if (JSON.stringify(candidate) !== JSON.stringify(baseline)) { restored = candidate; backupVersion = backup.version ?? 0; }
        }
      } catch { setBackupAvailable(false); }
      current.current = restored ?? baseline;
      setInput(current.current); setRecord(existing ?? null); setSaveError(null);
      const writer = new DraftSaver(draftId, existing?.draftVersion ?? 0, baseline, (value, version) => saveEventDraft(draftId, value, version),
        (state, error) => {
          if (!mounted.current || saver.current !== writer) return;
          setSaveState(state);
          setSaveError(state !== 'error' ? null : error instanceof ApiError && error.code === 'draft_conflict'
            ? 'This event changed in another tab. Your edits are kept on this device. Reload to check the saved draft before editing again.'
            : error instanceof ApiError && error.status === 401 ? 'Your session has expired. Your edits are kept on this device. Sign in again to resume saving.'
            : 'Could not save to your account. Your edits are kept on this device; we’ll retry when your connection returns.');
        },
        (saved, _snapshot, latest) => {
          try {
            const pending = JSON.parse(localStorage.getItem(key(email, draftId)) || 'null');
            // Another tab may have written its own recovery copy since this request began.
            if (latest && pending && JSON.stringify(pending.input) === JSON.stringify(_snapshot)) localStorage.removeItem(key(email, draftId));
            else if (!latest && pending && saver.current === writer && JSON.stringify(pending.input) === JSON.stringify(current.current)) localStorage.setItem(key(email, draftId), JSON.stringify({ ...pending, version: saved.draftVersion }));
            if (localStorage.getItem(key(email, 'new')) === draftId) localStorage.removeItem(key(email, 'new')); } catch { /* Server save succeeded. */ }
          if (!mounted.current || saver.current !== writer) return;
          setRecord(saved);
          if (location.pathname.endsWith('/events/new')) route(`${BASE_PATH}/events/${saved.id}${location.search}`, true);
        });
      saver.current = writer;
      if (restored) {
        setRecovered(true);
        if (existing && backupVersion !== existing.draftVersion) {
          setRecoveryConflict(existing); setSaveState('error');
          setSaveError('This event has newer saved changes as well as edits recovered from this device. Choose which draft to keep.');
        } else writer.change(restored);
      }
      if (existing && newEvent) route(`${BASE_PATH}/events/${existing.id}${location.search}`, true);
    };
    void load().catch((error) => { reportClientError('editor: load event', error, { eventId: id }); if (active) setLoadError('Could not load this event. Please reload and try again.'); });
    return () => { active = false; };
  }, [id, email]);

  const update = (next: EventInput) => {
    next = { ...next, dates: sortEventDates(next.dates), wardNames: sortWardNames(next.wardNames) };
    current.current = next;
    setInput(next);
    if (!email || !saver.current) return;
    try {
      localStorage.setItem(key(email, saver.current.id), JSON.stringify({ input: next, version: saver.current.version }));
      if (!record) localStorage.setItem(key(email, 'new'), saver.current.id);
    } catch { setBackupAvailable(false); }
    if (!recoveryConflict) saver.current.change(next);
  };
  const accept = (saved: EventRecord) => {
    const next = eventInput(saved);
    current.current = next; setInput(next); setRecord(saved); saver.current?.adopt(saved, next);
    try { if (email) localStorage.removeItem(key(email, saved.id)); } catch { /* Server save succeeded. */ }
  };
  const resolveRecovery = (keepLocal: boolean) => {
    if (!recoveryConflict) return;
    if (keepLocal && current.current) saver.current?.change(current.current);
    else accept(recoveryConflict);
    setRecoveryConflict(null); setSaveError(null); setRecovered(false);
  };
  return { input, record, history, update, accept, setRecord, saveState, saveError, loadError, recovered, backupAvailable, recoveryConflict, resolveRecovery,
    draftId: saver.current?.id, version: () => saver.current?.version ?? 0, flush: () => recoveryConflict ? Promise.reject(new ApiError(409, 'draft_conflict')) : saver.current?.flush() ?? Promise.resolve() };
}
